import { useEffect, useState, useCallback, useMemo } from 'react';
import { getTrustedUrls, isOriginAllowed } from '@demandproai/shared';

const isDevelopment = () => import.meta.env.VITE_IS_LOCAL_DEV === 'true';

const overlayClasses = `flex flex-col items-center justify-center
  h-screen p-5 text-center bg-surface-light
  font-[-apple-system,BlinkMacSystemFont,"Segoe_UI",Roboto,sans-serif]`;

const cardClasses = `bg-surface p-10 rounded-xl max-w-md
  shadow-[0_var(--spacing)_calc(var(--spacing)*5)_rgba(0,0,0,0.1)]`;

const iconWrapClasses = `w-15 h-15 rounded-full bg-danger-surface
  flex items-center justify-center mx-auto mb-5`;

const headingClasses = 'text-danger mb-3 text-xl font-semibold';

const messageClasses = 'text-disabled mb-4 leading-normal';

const devOriginClasses = `text-subtle text-xs bg-surface-light
  py-2 px-3 rounded-md break-all`;

export const useIframeSecurity = ({ 
    enforce = true,
    onUnauthorized = null
} = {}) => {
    const [isInIframe, setIsInIframe] = useState(false);
    const [isTrusted, setIsTrusted] = useState(true);
    const [parentOrigin, setParentOrigin] = useState(null);
    const [isChecking, setIsChecking] = useState(true);

    const allowedOrigins = useMemo(() => getTrustedUrls(isDevelopment()), []);

    const checkIframeSecurity = useCallback(() => {
        try {
            const inIframe = window.self !== window.top;
            setIsInIframe(inIframe);

            if (!inIframe) {
              
                setIsTrusted(true);
                setIsChecking(false);
                return;
            }

            let origin = null;
            try {
                origin = window.parent.location.origin;
            } catch {
                const referrer = document.referrer;
                if (referrer) {
                    try {
                        const referrerUrl = new URL(referrer);
                        origin = referrerUrl.origin;
                    } catch (parseError) {
                        console.warn(`[IFrame Security] Could not parse referrer: ${referrer}`, parseError);
                    }
                } else {
                    console.warn(`[IFrame Security] document.referrer is empty - cannot determine parent origin`);
                }
            }

            setParentOrigin(origin);
            
            if (!origin) {
                console.warn('[IFrame Security] Could not determine parent origin');
                
                if (isDevelopment()) {
                    console.warn('[IFrame Security] Development mode - allowing request without parent origin for testing');
                    setIsTrusted(true);
                } else {
                    console.warn('[IFrame Security] Production mode - blocking request without parent origin');
                    setIsTrusted(false);
                    if (enforce && onUnauthorized) {
                        onUnauthorized(null);
                    }
                }
            } else {
                const isAllowed = isOriginAllowed(origin, allowedOrigins);
                
                setIsTrusted(isAllowed);
                
                if (!isAllowed && enforce && onUnauthorized) {
                    onUnauthorized(origin);
                }
            }

            setIsChecking(false);
        } catch (error) {
            console.error('[IFrame Security] Error checking iframe security:', error);

            setIsTrusted(true);
            setIsChecking(false);
        }
    }, [enforce, onUnauthorized, allowedOrigins]);

    useEffect(() => {
        checkIframeSecurity();
    }, [checkIframeSecurity]);

    return {
        isInIframe,
        isTrusted,
        parentOrigin,
        isChecking,
        recheckSecurity: checkIframeSecurity
    };
};

export const IframeSecurityGuard = ({ children, enforce = true }) => {
    const handleUnauthorized = useCallback((origin) => {
        console.error(`[IFrame Security] Unauthorized iframe embedding from: ${origin || 'unknown'}`);
    }, []);

    const { isInIframe, isTrusted, parentOrigin, isChecking } = useIframeSecurity({ 
        enforce, 
        onUnauthorized: handleUnauthorized,

    });

    if (isChecking) {
        return null;
    }

    if (enforce && isInIframe && !isTrusted) {
        return (
            <div className={overlayClasses}>
                <div className={cardClasses}>
                    <div className={iconWrapClasses}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-danger)" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                    </div>
                    <h2 className={headingClasses}>
                        Access Denied
                    </h2>
                    <p className={messageClasses}>
                        This application can only be embedded from approved partner websites.
                    </p>
                    {isDevelopment() && parentOrigin && (
                        <p className={devOriginClasses}>
                            Detected origin: <code>{parentOrigin}</code>
                        </p>
                    )}
                </div>
            </div>
        );
    }

    return children;
};

export default useIframeSecurity;

